import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { InfoSectionContainer } from '@/common/InfoSection';
import { WBSButton } from '@/common/WBSButton';
import { Avatar } from '@/header/Avatar';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { StyledTextarea } from '~/styles/forms/formElements';

interface NoteInputProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  /**
   * A prop, unlike the avatar: the composer's label is the logged-in user's
   * display name, but a caller with a different source for it doesn't have to
   * fight the hook.
   */
  userName: string;
  /**
   * The textarea's placeholder, the only statement of what a note is for; it
   * differs per screen ("about this student" vs "about this group").
   */
  placeholder: string;
  /**
   * True while the caller's create is in flight; the second of the two submit
   * guards below.
   */
  isSubmitting?: boolean;
}

export function NoteInput({
  value,
  onChange,
  onSubmit,
  userName,
  placeholder,
  isSubmitting = false,
}: NoteInputProps) {
  const { t } = useTranslation();
  const { me } = useCurrentUser();
  const memberId = me?.id?.toString() || '';

  return (
    <NoteInputContainer>
      <AvatarNameRow to={`/members/${memberId}`}>
        {/* Derived, not a prop: the composer's author is always the
           logged-in user, and a prop would let a caller pass an avatar
           that contradicts the profile link around it. `NoteItem`'s avatar
           is a prop because its author varies per row. */}
        <Avatar size={50} src={me?.avatar} />
        <Name>{userName}</Name>
      </AvatarNameRow>
      <FullWidthTextarea
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <ButtonRow>
        {/* `disabled` matches the server, which trims content before
           requiring at least one character, so whitespace-only would be a
           422. `isLoading` is the second guard: creates have no
           idempotency key and the server accepts identical content twice,
           so a double-click would write two notes. */}
        <WBSButton
          wide
          onClick={onSubmit}
          disabled={!value.trim()}
          isLoading={isSubmitting}
        >
          {t('actions.add-note')}
        </WBSButton>
      </ButtonRow>
    </NoteInputContainer>
  );
}

const NoteInputContainer = styled(InfoSectionContainer)`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 21px;
  padding: 12px;
`;

const Name = styled.h2`
  color: var(--grey-900);
  font-family: var(--raleway);
  font-size: 14px;
  font-weight: 300;
  margin: 0;
`;

const AvatarNameRow = styled(Link)`
  align-items: center;
  align-self: flex-start;
  cursor: pointer;
  display: flex;
  gap: 12px;
  text-decoration: none;

  &:hover ${Name} {
    color: var(--wbs-red);
  }
`;

const FullWidthTextarea = styled(StyledTextarea)`
  font-size: 16px;
  font-weight: 300;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
`;
